/**
 * Two-step sign-in on the account page: setting up an authenticator app, the recovery codes
 * shown once, new codes, and turning it off where the account may go without it. The admin
 * meets the same setup before anything else once the server requires it (`TwoStepRequired`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { TwoStepSetup } from "@/api/models";
import { TWO_STEP_KEY, useTwoStep } from "@/api/queries";
import { PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SESSION_KEY, useSignOut } from "@/lib/session";
import { holdTitle } from "@/lib/title";
import { FormError } from "@/pages/sign-in";

/** The authenticator's six digits, typed or pasted. */
function CodeField({
  id,
  value,
  onChange,
  label = "6-digit code from the app",
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        required
        autoComplete="one-time-code"
        inputMode="numeric"
        pattern="[0-9 ]{6,7}"
        maxLength={7}
        spellCheck={false}
        className="max-w-48 font-mono text-lg tracking-[0.3em]"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** "JBSW Y3DP EHPK ...": the key in groups of four, easier to type into an app. */
function grouped(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(" ") ?? secret;
}

/** The recovery codes, shown this once, with ways to keep them and a promise they were kept. */
function RecoveryCodesShown({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [kept, setKept] = React.useState(false);
  const text = `EstateLark recovery codes\nEach code signs in once, in place of the code from your authenticator app.\n\n${codes.join("\n")}\n`;
  const download = () => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    link.download = "estatelark-recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <div className="flex flex-col gap-4" id="recovery-codes">
      <div className="flex flex-col gap-1">
        <p className="m-0 font-semibold text-heading">Save your recovery codes</p>
        <p className="m-0 text-sm text-muted">
          If you lose your phone, each code signs you in once. This is the only time they are shown. Keep them somewhere safe, such as your
          password manager.
        </p>
      </div>
      <ul className="m-0 grid max-w-md list-none grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-border bg-surface-soft p-4 font-mono text-sm text-heading">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          id="copy-recovery-codes"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(
              () => toast.success("Recovery codes copied."),
              () => toast.error("Copying was blocked. Download the codes instead."),
            );
          }}
        >
          Copy codes
        </Button>
        <Button type="button" variant="outline" id="download-recovery-codes" onClick={download}>
          Download codes
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm text-heading">
        <input
          type="checkbox"
          id="recovery-codes-kept"
          checked={kept}
          onChange={(event) => setKept(event.target.checked)}
          className="size-4 accent-(--ink)"
        />
        I have saved these codes
      </label>
      <div>
        <Button type="button" id="recovery-codes-done" disabled={!kept} onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

/** Scanning the QR code, or typing the key, then proving it with the app's first code. */
function SetUp({ onCancel, cancellable }: { onCancel: () => void; cancellable: boolean }) {
  const client = useQueryClient();
  const [setup, setSetup] = React.useState<TwoStepSetup | null>(null);
  const [code, setCode] = React.useState("");
  const [codes, setCodes] = React.useState<string[] | null>(null);
  const begin = useMutation({ mutationFn: api.beginTwoStep, onSuccess: setSetup });
  const confirm = useMutation({ mutationFn: () => api.confirmTwoStep(code), onSuccess: ({ codes }) => setCodes(codes) });
  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    begin.mutate();
  }, [begin]);
  if (codes) {
    return (
      <RecoveryCodesShown
        codes={codes}
        onDone={() => {
          // Read again only now, so the codes stay on screen until they are kept.
          void client.invalidateQueries({ queryKey: TWO_STEP_KEY });
          void client.invalidateQueries({ queryKey: SESSION_KEY });
          toast.success("Two-step sign-in is on.");
        }}
      />
    );
  }
  if (begin.isError) {
    return (
      <div className="flex flex-col gap-3">
        <FormError error={begin.error} />
        <div>
          <Button type="button" variant="outline" onClick={() => begin.mutate()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (!setup) return <p className="m-0 text-sm text-muted">Preparing your key</p>;
  return (
    <form
      className="flex flex-col gap-5"
      id="two-step-setup"
      onSubmit={(event) => {
        event.preventDefault();
        confirm.mutate();
      }}
    >
      <ol className="m-0 flex list-decimal flex-col gap-5 ps-5 text-sm text-heading marker:font-semibold">
        <li>
          <div className="flex flex-col gap-3">
            <span>
              Open an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password, and scan this
              code.
            </span>
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qr_svg)}`}
              alt="QR code for your authenticator app"
              id="two-step-qr"
              className="size-44 rounded-md border border-border bg-white p-1"
            />
            <span className="text-muted">
              Can't scan it? Enter this key in the app instead:{" "}
              <span className="font-mono text-heading" id="two-step-secret">
                {grouped(setup.secret)}
              </span>
            </span>
          </div>
        </li>
        <li>
          <div className="flex flex-col gap-3">
            <span>Type the 6-digit code the app shows for EstateLark.</span>
            <FormError error={confirm.error} />
            <CodeField id="two-step-first-code" value={code} onChange={setCode} />
          </div>
        </li>
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" id="two-step-confirm" disabled={confirm.isPending}>
          {confirm.isPending ? "Checking" : "Turn on two-step sign-in"}
        </Button>
        {cancellable ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** New recovery codes for a code from the app, replacing the earlier ones. */
function RenewCodes({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const [code, setCode] = React.useState("");
  const renew = useMutation({ mutationFn: () => api.renewRecoveryCodes(code) });
  if (renew.data) {
    return (
      <RecoveryCodesShown
        codes={renew.data.codes}
        onDone={() => {
          void client.invalidateQueries({ queryKey: TWO_STEP_KEY });
          onClose();
        }}
      />
    );
  }
  return (
    <form
      className="flex flex-col gap-4"
      id="renew-codes-form"
      onSubmit={(event) => {
        event.preventDefault();
        renew.mutate();
      }}
    >
      <p className="m-0 text-sm text-muted">Your earlier recovery codes stop working.</p>
      <FormError error={renew.error} />
      <CodeField id="renew-codes-code" value={code} onChange={setCode} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" id="renew-codes-confirm" disabled={renew.isPending}>
          Get new codes
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** Turning two-step sign-in off, with the password and a code. */
function TurnOff({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const off = useMutation({
    mutationFn: () => api.turnOffTwoStep(password, code),
    onSuccess: (state) => {
      client.setQueryData(TWO_STEP_KEY, state);
      void client.invalidateQueries({ queryKey: SESSION_KEY });
      toast.success("Two-step sign-in is off.");
      onClose();
    },
  });
  return (
    <form
      className="flex max-w-md flex-col gap-4"
      id="two-step-off-form"
      onSubmit={(event) => {
        event.preventDefault();
        off.mutate();
      }}
    >
      <p className="m-0 text-sm text-muted">Signing in will ask for your password only.</p>
      <FormError error={off.error} />
      <div className="flex flex-col gap-2">
        <Label htmlFor="two-step-off-password">Password</Label>
        <Input
          id="two-step-off-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <CodeField id="two-step-off-code" value={code} onChange={setCode} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="destructive" id="two-step-off-confirm" disabled={off.isPending}>
          Turn off two-step sign-in
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

type Opened = "none" | "setup" | "codes" | "off";

/** The account page's card: whether a code is asked for at sign-in, and the ways to change it. */
export function TwoStepCard({ required = false }: { required?: boolean }) {
  const state = useTwoStep();
  const [opened, setOpened] = React.useState<Opened>(required ? "setup" : "none");
  const found = state.data;
  if (!found) return null;
  let body: React.ReactNode;
  if (opened === "setup" && !found.enabled && found.available) {
    body = <SetUp cancellable={!required} onCancel={() => setOpened("none")} />;
  } else if (found.enabled && opened === "codes") {
    body = <RenewCodes onClose={() => setOpened("none")} />;
  } else if (found.enabled && opened === "off") {
    body = <TurnOff onClose={() => setOpened("none")} />;
  } else if (found.enabled) {
    body = (
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="m-0 flex flex-col gap-1 text-sm">
          <Badge variant="success" dot>
            On
          </Badge>
          <span className="text-muted" id="recovery-codes-left">
            {found.recovery_codes_left === 1 ? "1 recovery code left." : `${found.recovery_codes_left} recovery codes left.`}
          </span>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" id="renew-codes" onClick={() => setOpened("codes")}>
            New recovery codes
          </Button>
          {found.required ? null : (
            <Button variant="ghost" id="two-step-off" onClick={() => setOpened("off")}>
              Turn off
            </Button>
          )}
        </div>
      </div>
    );
  } else if (!found.available) {
    body = (
      <p className="m-0 text-sm text-muted" id="two-step-unavailable">
        {found.unavailable_reason}
      </p>
    );
  } else {
    body = (
      <div>
        <Button id="two-step-start" onClick={() => setOpened("setup")}>
          Set up two-step sign-in
        </Button>
      </div>
    );
  }
  return (
    <Card id="two-step">
      <div className="flex flex-col gap-1">
        <CardTitle>Two-step sign-in</CardTitle>
        <CardDescription>
          {found.required
            ? "Required for this account: signing in asks for your password and a code from your phone."
            : "Signing in asks for your password and a code from an app on your phone, so a stolen password is not enough."}
        </CardDescription>
      </div>
      {body}
    </Card>
  );
}

/** All the admin sees until two-step sign-in is on, once the server requires it. */
export function TwoStepRequired() {
  const signOut = useSignOut();
  React.useEffect(() => {
    holdTitle("Set up two-step sign-in | EstateLark");
    return () => holdTitle(null);
  }, []);
  return (
    <>
      <PageHeader
        title="Set up two-step sign-in"
        subtitle="This account can open every agent's account, so it needs a code from your phone as well as your password. It takes two minutes."
        actions={
          <Button variant="outline" onClick={() => signOut.mutate()} disabled={signOut.isPending} id="sign-out-btn">
            Sign out
          </Button>
        }
      />
      <TwoStepCard required />
    </>
  );
}
